import { useEffect } from 'react';
import { View, Pressable, Text, Platform, StatusBar } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { matchSportViewStyles as styles } from './MatchSportView.styles';
import { MatchSportWebView } from './MatchSportWebView';
import { MatchSportOverlays } from './MatchSportOverlays';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import { useMatchSportGame } from '../../../src/quiz/hooks/useMatchSportGame';
import { MATCH_SPORT_TEST_IDS } from '../../../src/quiz/constants/matchSport.constants';

interface MatchSportViewProps {
  onMoreGames: () => void;
}

/**
 * Fullscreen host for the Match Sport runtime (50-level match-3 campaign). The runtime is a
 * self-scaling document with its own map/briefing/board screens; the host only provides the back
 * control, the loading/error overlays, the coin toast, and the daily-cap banner.
 */
export function MatchSportView({ onMoreGames }: MatchSportViewProps) {
  const insets = useSafeAreaInsets();
  const game = useMatchSportGame({ insetTop: insets.top });

  // Hide status bar on native while the game is open.
  useEffect(() => {
    if (Platform.OS !== 'web') StatusBar.setHidden(true, 'fade');
    return () => { if (Platform.OS !== 'web') StatusBar.setHidden(false, 'fade'); };
  }, []);

  // Back leaves immediately, closing the funnel first (the runtime keeps its own map/retry screens).
  const handleBack = () => {
    game.confirmExit();
    onMoreGames();
  };

  return (
    <View style={styles.fullscreenContainer} testID={MATCH_SPORT_TEST_IDS.view}>
      {/* Back arrow — always top-left, on top of everything */}
      <Pressable
        testID={MATCH_SPORT_TEST_IDS.back}
        style={({ pressed }) => [styles.backButton, pressed && styles.pressedDim, { top: Math.max(8, insets.top) }]}
        onPress={handleBack}
      >
        <Text style={styles.backArrow}>←</Text>
      </Pressable>

      <View style={styles.canvasWrap}>
        <MatchSportWebView
          ref={(r) => game.bindWebView(r)}
          html={game.html}
          onMessage={game.onMessage}
        />
      </View>

      {game.capReached && (
        <View style={styles.capBanner} testID={MATCH_SPORT_TEST_IDS.capBanner} pointerEvents="none">
          <Text style={styles.capBannerText}>Daily coins capped — wins still count toward your campaign</Text>
        </View>
      )}

      <MatchSportOverlays status={game.status} onRetry={game.retry} />
      <WarmupToast message={game.toast} />
    </View>
  );
}
